import type { FacetParam, SelectedFacetParam } from "common/types";

import { DEFAULT_LEADS_SEARCH_PARAMS } from "../constants";

import type { LeadsSearchParams } from "../types";

type SearchParamKey = keyof LeadsSearchParams<SelectedFacetParam>;

export default function useLeadsSearchParams(
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  setSearchParams: (
    searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>,
  ) => void,
) {
  function toggleSearchParam(
    key: Exclude<SearchParamKey, "keywords" | "favorite">,
    value: SelectedFacetParam,
  ) {
    if (
      searchParams[key].some(({ id }) =>
        Array.isArray(value.id) && Array.isArray(id)
          ? value.id.every((string) => id.includes(string))
          : value.id === id,
      )
    ) {
      setSearchParams({
        [key]: (searchParams[key] as FacetParam[]).filter((item) =>
          Array.isArray(value.id) && Array.isArray(item.id)
            ? !value.id.every((string) => item.id.includes(string))
            : value.id !== item.id,
        ),
      });
    } else {
      setSearchParams({
        [key]: [
          ...(searchParams[key] as FacetParam[]),
          { id: value.id, name: value.name },
        ],
      });
    }
  }
  function resetSearchParam(param: SearchParamKey) {
    setSearchParams({
      [param]: DEFAULT_LEADS_SEARCH_PARAMS[param],
    });
  }
  function setSearchParam<T extends SearchParamKey>(
    key: T,
    value: LeadsSearchParams<SelectedFacetParam>[T],
  ) {
    setSearchParams({ [key]: value });
  }

  function resetFilters() {
    setSearchParams({
      ...DEFAULT_LEADS_SEARCH_PARAMS,
      keywords: searchParams.keywords,
    });
  }

  function resetParams() {
    setSearchParams(DEFAULT_LEADS_SEARCH_PARAMS);
  }
  const { keywords: _, ...filters } = searchParams;
  const hasFilters = Object.values(filters).some((filter) =>
    Array.isArray(filter) ? !!filter.length : !!filter,
  );

  return {
    setSearchParam,
    toggleSearchParam,
    resetSearchParam,
    hasFilters,
    resetFilters,
    resetParams,
  };
}
